import { type NotificationChannel } from '@apps/web/lib/notification_channel_features';
import {
    isNotificationChannelSupportedForType,
    type NotificationTypeKey,
} from '@apps/web/lib/notification_channel_preferences';
import {
    getNotificationSettingsGroupCheckedState,
    type NotificationSettingsSection,
} from '@apps/web/lib/notification_settings_catalog';

export const NOTIFICATION_CHANNEL_SLUGS = [
    'text',
    'email',
    'web',
    'voice',
] as const;

export type NotificationChannelSlug =
    (typeof NOTIFICATION_CHANNEL_SLUGS)[number];

export type NotificationChannelPage = {
    readonly slug: NotificationChannelSlug;
    readonly channel: NotificationChannel;
    readonly label: string;
};

const NOTIFICATION_CHANNEL_PAGES_BY_SLUG: Record<
    NotificationChannelSlug,
    NotificationChannelPage
> = {
    voice: { slug: 'voice', channel: 'voice', label: 'Voice calls' },
    email: { slug: 'email', channel: 'email', label: 'Email' },
    web: { slug: 'web', channel: 'inapp', label: 'Phoebe Website' },
    text: { slug: 'text', channel: 'sms', label: 'Text' },
};

export const NOTIFICATION_CHANNEL_PAGES: ReadonlyArray<NotificationChannelPage> =
    NOTIFICATION_CHANNEL_SLUGS.map(
        (slug) => NOTIFICATION_CHANNEL_PAGES_BY_SLUG[slug],
    );

export type NotificationSettingsEditor =
    | 'personal'
    | 'member'
    | 'bulk'
    | 'defaults';

// Every editor shows every channel. Team defaults apply to future teammates,
// so they never enroll anyone in phone calls.
const HIDDEN_CHANNELS_BY_EDITOR: Record<
    NotificationSettingsEditor,
    ReadonlyArray<NotificationChannel>
> = {
    personal: [],
    member: [],
    bulk: [],
    defaults: ['voice'],
};

export function notificationChannelPagesForEditor(
    editor: NotificationSettingsEditor,
): ReadonlyArray<NotificationChannelPage> {
    const hidden = HIDDEN_CHANNELS_BY_EDITOR[editor];
    return NOTIFICATION_CHANNEL_PAGES.filter(
        (page) => !hidden.includes(page.channel),
    );
}

export function isNotificationChannelEditable(
    editor: NotificationSettingsEditor,
    channel: NotificationChannel,
): boolean {
    return !HIDDEN_CHANNELS_BY_EDITOR[editor].includes(channel);
}

export function isNotificationChannelSlug(
    value: string,
): value is NotificationChannelSlug {
    return (NOTIFICATION_CHANNEL_SLUGS as ReadonlyArray<string>).includes(
        value,
    );
}

export function notificationChannelPageForSlug(
    slug: NotificationChannelSlug,
): NotificationChannelPage {
    return NOTIFICATION_CHANNEL_PAGES_BY_SLUG[slug];
}

export type ChannelPreferenceLookup = Partial<
    Record<
        NotificationTypeKey,
        { readonly [channel in NotificationChannel]?: boolean }
    >
>;

export function countEnabledChannelEvents(
    channel: NotificationChannel,
    preferences: ChannelPreferenceLookup | undefined,
    sections: ReadonlyArray<NotificationSettingsSection>,
): { readonly enabled: number; readonly available: number } {
    let enabled = 0;
    let available = 0;
    for (const section of sections) {
        for (const item of section.items) {
            if (!isNotificationChannelSupportedForType(item.key, channel)) {
                continue;
            }
            available += 1;
            const checked = getNotificationSettingsGroupCheckedState(
                item.keys,
                (key) => preferences?.[key]?.[channel] === true,
            );
            // Count settings rows, not flattened catalog keys, so the hub
            // number matches the switches on the channel page. A mixed group
            // is still "on" from the user's point of view.
            if (checked !== false) {
                enabled += 1;
            }
        }
    }
    return { enabled, available };
}

export function formatChannelHubStatus({
    enabled,
    available,
    smsNumberRequired,
}: {
    readonly enabled: number;
    readonly available: number;
    readonly smsNumberRequired: boolean;
}): string {
    if (smsNumberRequired) {
        return 'Set a number to enable texts';
    }
    return `${enabled} of ${available} enabled`;
}
